/*
 * Face ID / fingerprint login inside the Redbox mobile apps.
 * See docs/mobile/BIOMETRIC_LOGIN.md for the full flow and security model.
 *
 *  - After the user logs in with a password in the app, offer once: "להפעיל כניסה מהירה?"
 *    Yes → server issues a device key (/user/device/enroll) → the app stores it in the
 *    Keychain / Keystore behind Face ID / fingerprint.
 *  - When the app opens logged out and a key is stored → Face ID prompt → the app returns
 *    the key → /user/device/login creates a normal session → same as password login.
 *
 * All UI here is plain DOM (no React), shown only inside the app.
 */
import { getCookie, setCookie } from './Communicator';

const ENROLL_URL = '/user/device/enroll';
const LOGIN_URL = '/user/device/login';
const REVOKE_URL = '/user/device/revoke';
const PING_URL = '/user/userHandler';
const DECLINED_KEY = 'redboxBiometricDeclinedAt';
const DECLINE_DAYS = 30;

let callSeq = 0;
const pending = {};
let platform = 'android';
let suppressLoginThisLaunch = false;  // user chose password or logged out → don't nag again
let offeredEnableThisLaunch = false;
let sheetOpen = false;
let lastSession = null;

// ── Native bridge ──────────────────────────────────────────────────────────

window.__redboxNativeResult = (id, result) => {
    const cb = pending[id];
    if (cb) {
        delete pending[id];
        cb(result || { ok: false, status: 'error' });
    }
};

const bridgeAvailable = () =>
    (window.AndroidBridge && typeof window.AndroidBridge.biometric === 'function') ||
    !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeBridge);

/** Calls the app; resolves with { ok, status, ... }. Older app versions without the feature resolve { ok:false }. */
const nativeCall = (op, payload) =>
    new Promise((resolve) => {
        if (!bridgeAvailable()) return resolve({ ok: false, status: 'unsupported' });
        const id = `bio${++callSeq}`;
        pending[id] = resolve;
        try {
            if (window.AndroidBridge && typeof window.AndroidBridge.biometric === 'function') {
                window.AndroidBridge.biometric(op, payload || null, id);
            } else {
                window.webkit.messageHandlers.nativeBridge.postMessage({ action: 'biometric', op, payload: payload || null, callbackId: id });
            }
        } catch (e) {
            delete pending[id];
            resolve({ ok: false, status: 'error' });
        }
        // Status calls answer immediately; if an old iOS build ignores the message, don't hang forever.
        if (op === 'status') {
            setTimeout(() => window.__redboxNativeResult(id, { ok: false, status: 'unsupported' }), 3000);
        }
    });

const postJson = async (url, body) => {
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    let json = null;
    try { json = await res.json(); } catch (e) { /* empty */ }
    return { ok: res.ok, json };
};

const label = (type) =>
    type === 'faceID' ? 'Face ID' : type === 'touchID' ? 'Touch ID' : type === 'opticID' ? 'Optic ID' : 'טביעת אצבע / זיהוי פנים';

const currentSession = () => getCookie('SESSION_ID') || '';

/** The cookie can outlive the server session — ask the server. */
const isSessionActive = async (session) => {
    if (!session) return false;
    try {
        const { json } = await postJson(PING_URL, { opcode: 'ping', session });
        return !!(json && json.misc && json.misc.CODE === 1 && json.session);
    } catch (e) {
        return true; // offline: don't show a login prompt we can't complete anyway
    }
};

// ── UI (bottom sheet, RTL, Redbox colours) ─────────────────────────────────

const injectStyles = () => {
    if (document.getElementById('rb-bio-style')) return;
    const css = `
    .rb-bio-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:2147483000;display:flex;align-items:flex-end;justify-content:center;direction:rtl;font-family:Rubik,Assistant,Arial,sans-serif}
    .rb-bio-sheet{background:#fff;width:100%;max-width:480px;border-radius:20px 20px 0 0;padding:28px 24px calc(24px + env(safe-area-inset-bottom));box-sizing:border-box;text-align:center;box-shadow:0 -6px 24px rgba(0,0,0,.15)}
    .rb-bio-icon{width:56px;height:56px;margin:0 auto 12px;border-radius:50%;background:#ffe8eb;display:flex;align-items:center;justify-content:center}
    .rb-bio-title{font-size:20px;font-weight:700;color:#222;margin:0 0 8px}
    .rb-bio-text{font-size:15px;color:#555;margin:0 0 20px;line-height:1.5}
    .rb-bio-msg{font-size:14px;color:#c4001a;margin:-8px 0 16px;min-height:0}
    .rb-bio-btn{display:block;width:100%;border:0;border-radius:30px;padding:14px;font-size:17px;font-weight:600;cursor:pointer;margin-top:10px;font-family:inherit}
    .rb-bio-primary{background:#ff2138;color:#fff}
    .rb-bio-secondary{background:#f2f2f2;color:#333}
    .rb-bio-link{background:none;border:0;color:#888;font-size:13px;margin-top:14px;text-decoration:underline;cursor:pointer;font-family:inherit}
    .rb-bio-toast{position:fixed;bottom:calc(24px + env(safe-area-inset-bottom));left:50%;transform:translateX(-50%);background:#222;color:#fff;padding:12px 20px;border-radius:24px;font-size:15px;z-index:2147483001;direction:rtl;font-family:Rubik,Assistant,Arial,sans-serif;max-width:90%;text-align:center}`;
    const el = document.createElement('style');
    el.id = 'rb-bio-style';
    el.textContent = css;
    document.head.appendChild(el);
};

const ICON = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ff2138" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"/><circle cx="9" cy="10" r=".5"/><circle cx="15" cy="10" r=".5"/><path d="M12 9v4h-1M9 16c1.7 1.3 4.3 1.3 6 0"/></svg>';

const el = (tag, cls, text) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
};

/** Shows a sheet; buttons: [{ text, cls, onClick }]. Returns { close, setMessage }. */
const showSheet = ({ title, text, buttons, link }) => {
    injectStyles();
    sheetOpen = true;
    const backdrop = el('div', 'rb-bio-backdrop');
    const sheet = el('div', 'rb-bio-sheet');
    const icon = el('div', 'rb-bio-icon');
    icon.innerHTML = ICON;
    const msg = el('p', 'rb-bio-msg');
    sheet.append(icon, el('h2', 'rb-bio-title', title), el('p', 'rb-bio-text', text), msg);
    const close = () => {
        sheetOpen = false;
        backdrop.remove();
    };
    buttons.forEach((b) => {
        const btn = el('button', `rb-bio-btn ${b.cls}`, b.text);
        btn.type = 'button';
        btn.onclick = () => b.onClick({ close, setMessage });
        sheet.appendChild(btn);
    });
    if (link) {
        const a = el('button', 'rb-bio-link', link.text);
        a.type = 'button';
        a.onclick = () => link.onClick({ close, setMessage });
        sheet.appendChild(a);
    }
    function setMessage(t) { msg.textContent = t || ''; }
    backdrop.appendChild(sheet);
    document.body.appendChild(backdrop);
    return { close, setMessage };
};

const toast = (text) => {
    injectStyles();
    const t = el('div', 'rb-bio-toast', text);
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3500);
};

// ── Login with Face ID / fingerprint ───────────────────────────────────────

const unlockAndLogin = async (ui) => {
    ui.setMessage('');
    const r = await nativeCall('unlock');
    if (r.ok && r.deviceKey) {
        try {
            const { json } = await postJson(LOGIN_URL, { deviceKey: r.deviceKey });
            if (json && json.misc && json.misc.CODE === 1 && json.session) {
                setCookie('SESSION_ID', json.session, 30); // same as password login (Login.js)
                window.location.reload();
                return;
            }
            // Key rejected by the server (revoked, password changed, expired).
            await nativeCall('disable');
            ui.close();
            suppressLoginThisLaunch = true;
            toast('הכניסה המהירה אינה פעילה יותר. יש להתחבר עם סיסמה.');
        } catch (e) {
            ui.setMessage('אין חיבור לשרת. נסו שוב.');
        }
        return;
    }
    if (r.status === 'invalidated') {
        ui.close();
        suppressLoginThisLaunch = true;
        toast('הגדרות הזיהוי במכשיר השתנו. יש להתחבר עם סיסמה ולהפעיל מחדש.');
    } else if (r.status === 'error') {
        ui.setMessage('הזיהוי נכשל. נסו שוב או התחברו עם סיסמה.');
    }
    // cancelled → leave the sheet open with the buttons
};

export const offerBiometricLogin = async () => {
    if (suppressLoginThisLaunch || sheetOpen) return;
    const session = currentSession();
    if (session && (await isSessionActive(session))) return;
    const status = await nativeCall('status');
    if (!status.ok || !status.available || !status.enrolled) return;

    const name = label(status.type);
    const ui = showSheet({
        title: 'כניסה מהירה',
        text: `התחברו לחשבון Redbox שלכם עם ${name}`,
        buttons: [
            { text: `כניסה עם ${name}`, cls: 'rb-bio-primary', onClick: (s) => unlockAndLogin(s) },
            { text: 'כניסה עם סיסמה', cls: 'rb-bio-secondary', onClick: (s) => { suppressLoginThisLaunch = true; s.close(); } },
        ],
        link: {
            text: 'הסרת הכניסה המהירה ממכשיר זה',
            onClick: async (s) => {
                await nativeCall('disable');
                suppressLoginThisLaunch = true;
                s.close();
                toast('הכניסה המהירה הוסרה מהמכשיר.');
            },
        },
    });
    unlockAndLogin(ui); // open the Face ID / fingerprint prompt right away
};

// ── Offer to enable after a password login ─────────────────────────────────

const recentlyDeclined = () => {
    try {
        const t = Number(localStorage.getItem(DECLINED_KEY) || 0);
        return t && Date.now() - t < DECLINE_DAYS * 24 * 3600 * 1000;
    } catch (e) { return false; }
};

const enable = async (ui, session) => {
    ui.setMessage('');
    let deviceKey = null;
    try {
        const { ok, json } = await postJson(ENROLL_URL, { session, platform, deviceName: platform });
        deviceKey = ok && json && json.deviceKey;
    } catch (e) { /* handled below */ }
    if (!deviceKey) {
        ui.setMessage('לא ניתן להפעיל כרגע. נסו שוב מאוחר יותר.');
        return;
    }
    const r = await nativeCall('enable', deviceKey);
    if (r.ok) {
        ui.close();
        toast('הכניסה המהירה הופעלה ✓');
        return;
    }
    postJson(REVOKE_URL, { deviceKey }).catch(() => {}); // not stored on the phone → drop it on the server
    if (r.status === 'cancelled') return;      // sheet stays, user can retry or say "not now"
    ui.setMessage('ההפעלה נכשלה. נסו שוב.');
};

export const offerBiometricEnable = async () => {
    if (offeredEnableThisLaunch || sheetOpen || recentlyDeclined()) return;
    const session = currentSession();
    if (!session) return;
    const status = await nativeCall('status');
    if (!status.ok || !status.available || status.enrolled) return;
    if (!(await isSessionActive(session))) return;
    offeredEnableThisLaunch = true;

    const name = label(status.type);
    showSheet({
        title: `כניסה מהירה עם ${name}`,
        text: `בפעם הבאה תוכלו להיכנס לחשבון עם ${name}, בלי להקליד סיסמה.`,
        buttons: [
            { text: 'הפעלה', cls: 'rb-bio-primary', onClick: (s) => enable(s, session) },
            {
                text: 'לא עכשיו',
                cls: 'rb-bio-secondary',
                onClick: (s) => {
                    try { localStorage.setItem(DECLINED_KEY, String(Date.now())); } catch (e) { /* ignore */ }
                    s.close();
                },
            },
        ],
    });
};

// ── Wiring ─────────────────────────────────────────────────────────────────

/** Called periodically and on resume by nativeApp.js. Reacts to login / logout. */
const LAST_SESSION_KEY = 'redboxLastSession'; // sessionStorage: survives page reloads, not app restarts

const rememberSession = (session) => {
    try { sessionStorage.setItem(LAST_SESSION_KEY, session); } catch (e) { /* ignore */ }
};

export const checkBiometric = () => {
    const session = currentSession();
    if (lastSession === null) {
        lastSession = session;
        rememberSession(session);
        return;
    }
    if (!lastSession && session) offerBiometricEnable();   // just logged in with password
    if (lastSession && !session) suppressLoginThisLaunch = true; // just logged out: don't pop Face ID at once
    lastSession = session;
    rememberSession(session);
};

export const initBiometric = (pf) => {
    platform = pf;
    // The site reloads the page on logout — don't greet the user with Face ID right after logging out.
    let previous = '';
    try { previous = sessionStorage.getItem(LAST_SESSION_KEY) || ''; } catch (e) { /* ignore */ }
    if (previous && !currentSession()) suppressLoginThisLaunch = true;

    // Give the page a moment to render and run its own session ping.
    setTimeout(async () => {
        lastSession = currentSession();
        rememberSession(lastSession);
        if (lastSession && (await isSessionActive(lastSession))) offerBiometricEnable();
        else offerBiometricLogin();
    }, 1500);
};

/** App returned to the foreground. */
export const onResumeBiometric = () => {
    if (!currentSession()) offerBiometricLogin();
};
